import React, {
  useEffect,
  useState,
} from "react";

import "./AdminProducts.css";

import Sidebar from "../../components/Sidebar";
import ProductForm from "./ProductForm";
import ProductTable from "./ProductTable";
import ProductModal from "./ProductModal";

import {
  fetchProducts,
  createProduct,
  updateProduct,
  deleteProduct,
} from "../../graphql/api/productService";

import {
  fetchCategoriesAndSubcategories,
} from "../../graphql/api/categoryService";

import {
  Product,
  ProductInput,
  Category,
  Subcategory,
  FieldItem,
} from "../../interfaces/adminproduct";

import {
  fetchShops,
  fetchVendors,
} from "../../graphql/api/VendorApi";

import Loading from "../../components/Loading";
import { useAuth } from "../../context/AuthContext";


/* =========================================================
   VENDOR PRODUCTS
   ========================================================= */

const VendorProducts: React.FC = () => {

  /* =======================================================
     STATE
     ======================================================= */

  const [products, setProducts] =
    useState<Product[]>([]);

  const [categories, setCategories] =
    useState<Category[]>([]);

  const [subcategories, setSubcategories] =
    useState<Subcategory[]>([]);

  const [shops, setShops] =
    useState<any[]>([]);

  const [vendors, setVendors] =
    useState<any[]>([]);


  /* =======================================================
     FORM DATA
     ======================================================= */

  const [formData, setFormData] =
    useState<Product>({
      ...defaultFormData,
    });


  /* =======================================================
     IMAGE STATE

     existingImages:
       Images already stored in database.

     imageFiles:
       Newly selected files.

     imagePreviews:
       What is displayed in the UI.
       Contains existing + newly selected images.
     ======================================================= */

  const [existingImages, setExistingImages] =
    useState<string[]>([]);

  const [imagePreviews, setImagePreviews] =
    useState<string[]>([]);

  const [imageFiles, setImageFiles] =
    useState<File[]>([]);


  /* =======================================================
     EDIT / TABLE STATE
     ======================================================= */

  const [isEditing, setIsEditing] =
    useState(false);

  const [searchTerm, setSearchTerm] =
    useState("");

  const [modalIsOpen, setModalIsOpen] =
    useState(false);

  const [selectedProduct, setSelectedProduct] =
    useState<Product | null>(null);


  /* =======================================================
     SIDEBAR
     ======================================================= */

  const [isSidebarOpen, setIsSidebarOpen] =
    useState(false);

  const [isMobile, setIsMobile] =
    useState(
      window.innerWidth <= 768
    );


  /* =======================================================
     LOADING
     ======================================================= */

  const [loading, setLoading] =
    useState(false);


  /* =======================================================
     AUTH
     ======================================================= */

  const { currentUser } =
    useAuth();


  /* =======================================================
     FORM FIELDS

     These match the Admin Product page.

     1. Product Name
     2. Price
     3. Stock Quantity
     4. Category
     5. Description
     6. Images
     ======================================================= */

  const fieldTabs: FieldItem[][] = [
    [
      {
        name: "name",
        label: "Product Name",
        type: "text",
      },

      {
        name: "price",
        label: "Price",
        type: "number",
      },

      {
        name: "stockQuantity",
        label: "Stock Quantity",
        type: "number",
      },

      {
        name: "categoryId",
        label: "Category",
        type: "select",
      },
    ],

    [
      {
        name: "description",
        label: "Description",
        type: "textarea",
      },
    ],
  ];


  /* =======================================================
     LOAD DATA
     ======================================================= */

  const loadData = async () => {

    if (!currentUser?.id) {
      console.warn(
        "No current user found"
      );

      return;
    }

    setLoading(true);

    try {

      /* -----------------------------------------------
         PRODUCTS
         ----------------------------------------------- */

      const productsData =
        await fetchProducts();


      /* -----------------------------------------------
         CATEGORIES
         ----------------------------------------------- */

      const {
        categories,
        subcategories,
      } =
        await fetchCategoriesAndSubcategories();


      /* -----------------------------------------------
         VENDORS
         ----------------------------------------------- */

      const allVendors =
        await fetchVendors();


      /* -----------------------------------------------
         SHOPS
         ----------------------------------------------- */

      const allShops =
        await fetchShops();


      /* -----------------------------------------------
         VENDORS BELONGING TO CURRENT USER
         ----------------------------------------------- */

      const myVendors =
        allVendors.filter(
          (vendor: any) =>
            vendor.userId ===
            currentUser.id
        );


      /* -----------------------------------------------
         SHOPS BELONGING TO CURRENT USER
         ----------------------------------------------- */

      const myShops =
        allShops.filter(
          (shop: any) =>
            shop.vendorId ===
            currentUser.id
        );


      /* -----------------------------------------------
         VENDOR IDS
         ----------------------------------------------- */

      const myVendorIds =
        myVendors.map(
          (vendor: any) =>
            vendor.id
        );


      /* -----------------------------------------------
         ONLY SHOW PRODUCTS BELONGING
         TO CURRENT VENDOR
         ----------------------------------------------- */

      const filteredProducts =
        productsData.filter(
          (product: Product) =>
            myVendorIds.includes(
              product.vendorId
            )
        );


      /* -----------------------------------------------
         SET STATE
         ----------------------------------------------- */

      setProducts(
        filteredProducts
      );

      setCategories(
        categories
      );

      setSubcategories(
        subcategories
      );

      setVendors(
        myVendors
      );

      setShops(
        myShops
      );


    } catch (error) {

      console.error(
        "Error loading vendor products:",
        error
      );

    } finally {

      setLoading(false);

    }
  };


  /* =======================================================
     INITIAL LOAD
     ======================================================= */

  useEffect(() => {

    if (currentUser?.id) {
      loadData();
    }


    const handleResize = () => {

      setIsMobile(
        window.innerWidth <= 768
      );

    };


    window.addEventListener(
      "resize",
      handleResize
    );


    return () => {

      window.removeEventListener(
        "resize",
        handleResize
      );

    };

  }, [currentUser?.id]);


  /* =======================================================
     IMAGE CHANGE

     IMPORTANT:
     Existing images are NOT removed.

     New files are appended.

     Example:

       Existing:
       image1.jpg
       image2.jpg

       Select:
       image3.jpg

       Result:
       image1.jpg
       image2.jpg
       image3.jpg
     ======================================================= */

  const handleImageChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {

    const files =
      Array.from(
        e.target.files || []
      );


    if (
      files.length === 0
    ) {
      return;
    }


    /* -----------------------------------------------
       APPEND NEW FILES
       ----------------------------------------------- */

    setImageFiles(
      (previousFiles) => [
        ...previousFiles,
        ...files,
      ]
    );


    /* -----------------------------------------------
       CREATE NEW PREVIEWS
       ----------------------------------------------- */

    const newPreviews =
      files.map(
        (file) =>
          URL.createObjectURL(file)
      );


    /* -----------------------------------------------
       APPEND NEW PREVIEWS

       Existing server images remain.
       ----------------------------------------------- */

    setImagePreviews(
      (previousPreviews) => [
        ...previousPreviews,
        ...newPreviews,
      ]
    );


    /*
     * Clear input value.
     *
     * This allows selecting the same
     * file again.
     */

    e.target.value = "";
  };


  /* =======================================================
     RESET FORM
     ======================================================= */

  const resetForm = () => {

    setFormData({

      ...defaultFormData,

      vendorId:
        vendors.length > 0
          ? vendors[0].id
          : "",

    });


    /* -----------------------------------------------
       CLEAR IMAGE STATE
       ----------------------------------------------- */

    setExistingImages([]);

    setImageFiles([]);

    setImagePreviews([]);


    setIsEditing(false);
  };


  /* =======================================================
     EDIT PRODUCT
     ======================================================= */

  const handleEdit = (
    product: Product
  ) => {

    /* -----------------------------------------------
       LOAD PRODUCT INTO FORM
       ----------------------------------------------- */

    const serverImages = product.images || [];

setFormData({
  ...product,

  images: serverImages,

  attributes:
    product.attributes ?? [],

  variants:
    product.variants ?? [],
});


    /* -----------------------------------------------
       EDIT MODE
       ----------------------------------------------- */

    setIsEditing(true);


    /* -----------------------------------------------
       EXISTING SERVER IMAGES
       ----------------------------------------------- */

    // const serverImages =
    //   product.images || [];


    setExistingImages(
      serverImages
    );


    /*
     * Existing images are shown
     * immediately in the upload area.
     */

    setImagePreviews(
      serverImages
    );


    /*
     * No new files selected yet.
     */

    setImageFiles([]);


    /* -----------------------------------------------
       SCROLL TO PRODUCT FORM
       ----------------------------------------------- */

    window.scrollTo({

      top: 0,

      behavior: "smooth",

    });
  };


  /* =======================================================
     DELETE PRODUCT
     ======================================================= */

  const handleDelete = async (
    id: string
  ) => {

    try {

      await deleteProduct(id);


      setProducts(
        (previousProducts) =>
          previousProducts.filter(
            (product) =>
              product.id !== id
          )
      );


    } catch (error) {

      console.error(
        "Error deleting product:",
        error
      );

    }
  };


  /* =======================================================
     CREATE / UPDATE PRODUCT
     ======================================================= */

  const handleSubmit = async (
    e?: React.FormEvent
  ) => {

    e?.preventDefault();


    try {

      let savedProduct: Product;


      /* =================================================
         UPDATE PRODUCT
         ================================================= */

      // if (isEditing) {

      //   const {

      //     id,

      //     images: productImages,

      //     ...rest

      //   } = formData;


      //   /* -----------------------------------------------
      //      UPDATE WITH NEW IMAGES
      //      ----------------------------------------------- */

      //   if (
      //     imageFiles.length > 0
      //   ) {

      //     const fd =
      //       new FormData();


      //     /* ---------------------------------------------
      //        ADD PRODUCT FIELDS
      //        --------------------------------------------- */

      //     Object.entries(
      //       rest
      //     ).forEach(
      //       ([key, value]) => {

      //         if (
      //           value === null ||
      //           value === undefined
      //         ) {
      //           return;
      //         }


      //         /* -----------------------------------------
      //            ARRAYS / OBJECTS
      //            ----------------------------------------- */

      //         if (
      //           key ===
      //             "variants" ||
      //           key ===
      //             "attributes"
      //         ) {

      //           fd.append(
      //             key,
      //             JSON.stringify(
      //               value
      //             )
      //           );

      //           return;
      //         }


      //         /* -----------------------------------------
      //            NUMBERS
      //            ----------------------------------------- */

      //         if (
      //           typeof value ===
      //           "number"
      //         ) {

      //           fd.append(
      //             key,
      //             value.toString()
      //           );

      //           return;
      //         }


      //         /* -----------------------------------------
      //            STRINGS
      //            ----------------------------------------- */

      //         if (
      //           typeof value ===
      //           "string"
      //         ) {

      //           fd.append(
      //             key,
      //             value
      //           );

      //         }

      //       }
      //     );


      //     /* ---------------------------------------------
      //        SLUG
      //        --------------------------------------------- */

      //     if (
      //       rest.name
      //     ) {

      //       const slug =
      //         rest.name
      //           .trim()
      //           .toLowerCase()
      //           .replace(
      //             /\s+/g,
      //             "-"
      //           );


      //       fd.append(
      //         "slug",
      //         slug
      //       );

      //     }


      //     /* ---------------------------------------------
      //        KEEP EXISTING IMAGES

      //        These are server URLs.

      //        They must not be replaced just because
      //        new images were selected.
      //        --------------------------------------------- */

      //     fd.append(
      //       "images",
      //       JSON.stringify(
      //         existingImages
      //       )
      //     );


      //     /* ---------------------------------------------
      //        ADD NEW IMAGE FILES
      //        --------------------------------------------- */

      //     imageFiles.forEach(
      //       (file) => {

      //         fd.append(
      //           "image",
      //           file
      //         );

      //       }
      //     );


      //     /* ---------------------------------------------
      //        UPDATE API
      //        --------------------------------------------- */

      //     savedProduct =
      //       await updateProduct(
      //         id,
      //         fd
      //       );

      //   }

      //   /* =================================================
      //      UPDATE WITHOUT NEW IMAGES
      //      ================================================= */

      //   else {

      //     const preparedData:
      //       ProductInput =
      //       {

      //         ...rest,

      //         slug:
      //           rest.name
      //             .trim()
      //             .toLowerCase()
      //             .replace(
      //               /\s+/g,
      //               "-"
      //             ),

      //         attributes:
      //           rest.attributes ??
      //           [],

      //         variants:
      //           rest.variants ??
      //           [],

      //         /*
      //          * Keep existing images.
      //          */

      //         images:
      //           existingImages,

      //       };


      //     savedProduct =
      //       await updateProduct(
      //         id,
      //         preparedData
      //       );

      //   }


      //   /* -----------------------------------------------
      //      UPDATE LOCAL TABLE
      //      ----------------------------------------------- */

      //   setProducts(
      //     (previousProducts) =>
      //       previousProducts.map(
      //         (product) =>
      //           product.id ===
      //           savedProduct.id
      //             ? savedProduct
      //             : product
      //       )
      //   );

      // }
      if (isEditing) {
  const {
    id,
    images: productImages,
    ...rest
  } = formData;

  const fd = new FormData();

  // ---------------------------------------------
  // PRODUCT FIELDS
  // ---------------------------------------------
  Object.entries(rest).forEach(([key, value]) => {
    if (
      value === null ||
      value === undefined
    ) {
      return;
    }

    // Arrays / objects
    if (
      key === "variants" ||
      key === "attributes"
    ) {
      fd.append(
        key,
        JSON.stringify(value)
      );
      return;
    }

    // Numbers
    if (typeof value === "number") {
      fd.append(
        key,
        value.toString()
      );
      return;
    }

    // Strings
    if (typeof value === "string") {
      fd.append(key, value);
      return;
    }

    // Boolean
    if (typeof value === "boolean") {
      fd.append(
        key,
        value.toString()
      );
    }
  });

  // ---------------------------------------------
  // SLUG
  // ---------------------------------------------
  if (rest.name) {
    const slug = rest.name
      .trim()
      .toLowerCase()
      .replace(/\s+/g, "-");

    fd.append("slug", slug);
  }

  // ---------------------------------------------
  // EXISTING IMAGES
  // ---------------------------------------------
  //
  // These are the images already stored in DB.
  // Send them even when NO new image is selected.
  //
  fd.append(
    "images",
    JSON.stringify(existingImages)
  );

  // ---------------------------------------------
  // NEW IMAGE FILES
  // ---------------------------------------------
  //
  // If user selected new images, append them.
  // Existing images remain in `images`.
  //
  imageFiles.forEach((file) => {
    fd.append("image", file);
  });

  // ---------------------------------------------
  // DEBUG
  // ---------------------------------------------
  console.log(
    "Existing images:",
    existingImages
  );

  console.log(
    "New image files:",
    imageFiles
  );

  for (const [key, value] of fd.entries()) {
    console.log(
      "FORM DATA:",
      key,
      value
    );
  }

  // ---------------------------------------------
  // UPDATE
  // ---------------------------------------------
  savedProduct = await updateProduct(
    id,
    fd
  );

  // ---------------------------------------------
  // UPDATE LOCAL TABLE
  // ---------------------------------------------
  setProducts(
    (previousProducts) =>
      previousProducts.map(
        (product) =>
          product.id === savedProduct.id
            ? savedProduct
            : product
      )
  );
}


      /* =================================================
         CREATE PRODUCT
         ================================================= */

      else {

        const fd =
          new FormData();


        /* -----------------------------------------------
           ADD FORM FIELDS
           ----------------------------------------------- */

        Object.entries(
          formData
        ).forEach(
          ([key, value]) => {

            /*
             * ID should not be sent.
             */

            if (
              key === "id" ||
              value === null ||
              value === undefined
            ) {

              return;

            }


            /*
             * Images are handled separately.
             */

            if (
              key === "images"
            ) {

              return;

            }


            /*
             * Attributes / variants
             */

            if (
              key ===
                "attributes" ||
              key ===
                "variants"
            ) {

              fd.append(
                key,
                JSON.stringify(
                  value
                )
              );

              return;

            }


            /*
             * Numbers
             */

            if (
              typeof value ===
              "number"
            ) {

              fd.append(
                key,
                value.toString()
              );

              return;

            }


            /*
             * Strings
             */

            if (
              typeof value ===
              "string"
            ) {

              fd.append(
                key,
                value
              );

            }

          }
        );


        /* -----------------------------------------------
           GENERATE SLUG
           ----------------------------------------------- */

        if (
          formData.name
        ) {

          const slug =
            formData.name
              .trim()
              .toLowerCase()
              .replace(
                /\s+/g,
                "-"
              );


          fd.append(
            "slug",
            slug
          );

        }


        /* -----------------------------------------------
           ADD IMAGE FILES
           ----------------------------------------------- */

        imageFiles.forEach(
          (file) => {

            fd.append(
              "image",
              file
            );

          }
        );


        /* -----------------------------------------------
           CREATE API
           ----------------------------------------------- */

        savedProduct =
          await createProduct(
            fd
          );


        /* -----------------------------------------------
           ADD TO TABLE
           ----------------------------------------------- */

        setProducts(
          (previousProducts) => [
            ...previousProducts,
            savedProduct,
          ]
        );

      }


      /* =================================================
         RESET
         ================================================= */

      resetForm();


    } catch (error) {

      console.error(
        "Error saving vendor product:",
        error
      );

    }

  };


  /* =======================================================
     RENDER
     ======================================================= */

  return (

    <div
      className="admin-container"
      style={{
        display: "flex",
      }}
    >

      {/* =================================================
          SIDEBAR
          ================================================= */}

      <Sidebar
        isOpen={
          isSidebarOpen
        }
        setIsOpen={
          setIsSidebarOpen
        }
      />


      {/* =================================================
          PRODUCT MODAL
          ================================================= */}

      <ProductModal

        isOpen={
          modalIsOpen
        }

        onClose={() =>
          setModalIsOpen(
            false
          )
        }

        product={
          selectedProduct
        }

      />


      {/* =================================================
          MAIN CONTENT
          ================================================= */}

      <div
        style={{

          flex: 1,

          marginLeft:
            isSidebarOpen
              ? isMobile
                ? "0px"
                : "250px"
              : isMobile
              ? "0px"
              : "60px",

          transition:
            "margin-left 0.3s ease",

          padding: "20px",

        }}
      >

        <div
          className="admin-products"
        >

          {/* =================================================
              LOADING
              ================================================= */}

          {loading && (
            <Loading />
          )}


          {/* =================================================
              PRODUCT FORM
              ================================================= */}

          <ProductForm

            formData={
              formData
            }

            setFormData={
              setFormData
            }

            isEditing={
              isEditing
            }

            handleSubmit={
              handleSubmit
            }

            resetForm={
              resetForm
            }

            setIsEditing={
              setIsEditing
            }

            /*
             * IMPORTANT:
             *
             * imagePreviews contains:
             *
             * Existing server images
             * +
             * New selected image previews
             */

            imagePreviews={
              imagePreviews
            }

            handleImageChange={
              handleImageChange
            }

            handleVideoChange={
              () => {}
            }

            categories={
              categories
            }

            shops={
              shops
            }

            vendors={
              vendors
            }

            fieldTabs={
              fieldTabs
            }

          />


          {/* =================================================
              PRODUCT TABLE
              ================================================= */}

          <ProductTable

            myrole="vendor"

            products={
              products
            }

            categories={
              categories
            }

            searchTerm={
              searchTerm
            }

            setSearchTerm={
              setSearchTerm
            }

            handleEdit={
              handleEdit
            }

            handleDelete={
              handleDelete
            }

            openModal={(
              product
            ) => {

              setSelectedProduct(
                product
              );

              setModalIsOpen(
                true
              );

            }}

          />

        </div>

      </div>

    </div>
  );
};


/* =========================================================
   DEFAULT FORM DATA
   ========================================================= */

const defaultFormData: Product = {

  id: "",

  name: "",

  slug: "",

  description: "",

  price: 0,

  discountedPrice: 0,

  currency: "INR",

  stockQuantity: 0,

  categoryId: "",

  vendorId: "",

  shopId: "",

  images: [],

  attributes: [],

  variants: [],

  status: "",

  rewardFlag: false,

  marginPrice: 0,

};


export default VendorProducts;